import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from '../Header/Header';
import DocMenu from '../DocMenu';
import ListTable from './Global/ListTable';
import GenPdf from '../GenPdf';
import { genSyntheseTablePDF, getSyntheseTablePDF, getCodeFacture } from './Utils';
import { handleChangeDateToMonth } from "../Utils";
import { fetchAllReminder } from "../../redux/actions/reminderActions";
import { fetchAllFac } from '../../redux/actions/facActions';
import { changeTitleName, changePdfFileName } from "../../redux/actions/emailAction";
import "../../Styles/Factures/Global.scss";

// Utilitaire global : parse JSON uniquement si c'est encore une string
const safeParseJson = (value) => {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
};

const Global = () => {
  const dispatch = useDispatch();
  const factures = useSelector(state => state.facture.allfacture);
  const reminders = useSelector(state => state.reminder.items);
  const facturations = useSelector(state => state.facture.items);
  const deviscon = useSelector(state => state.devis.deviscon);
  const devisrea = useSelector(state => state.devis.devisrea);
  const dossierValue = useSelector(state => state.devis.dossierValue);
  const factureIndex = useSelector(state => state.facture.facindex);
  const dossiers = useSelector(state => state.dossier.items);
  const contracts = useSelector(state => state.contrat.items);
  const echeance = useSelector(state => state.echeances.itemNdossier);

  const [facturesData, setFacturesData] = useState([]);
  const [openFile, setOpenFile] = useState(false);
  const [file, setFile] = useState(null);
  const [disableButton, setDisableButton] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalBredAmount, setTotalBredAmount] = useState(0);
  const [totalCaAmount, setTotalCaAmount] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filtersStatut, setFiltersStatut] = useState({
    firstReminder: false,
    secondReminder: false,
    toSend: false,
    paye: false,
    nonPaye: false,
  });

  const isMounted = useRef(false);
  const currentLoadDataId = useRef(null);
  const isFirstRender = useRef(true);

  // ─── Utilitaires ──────────────────────────────────────────────────────────
  const formatDate = useCallback(
    (date) => date ? new Date(date).toLocaleDateString('en-CA') : null,
    []
  );

  const getTotalAmount = useCallback(
    (data) => data.reduce((total, f) => total + f.amount_ht, 0),
    []
  );

  const getTotalAmountByBank = useCallback((data) => ({
    bredAmount: data.reduce((total, f) => total + +f.amount_bred, 0),
    caAmount: data.reduce((total, f) => total + +f.amount_ca, 0),
  }), []);

  // ─── handleGetDateReminder ────────────────────────────────────────────────
  const handleGetDateReminder = useCallback((propsItem, refFacture, firstReminder) => {
    const { validation, date_ech, ndossier } = propsItem;
    const today = new Date();
    const dateFormated = new Date(date_ech);
    const interval = firstReminder ? 15 : 30;
    const formatRef = refFacture !== "C1" && refFacture !== "C2"
      ? `${refFacture} ` : refFacture;

    dateFormated.setDate(dateFormated.getDate() + interval);

    const current = reminders.filter(
      item => item.ndossier === ndossier &&
        item.first_reminder === firstReminder &&
        item.numclient.includes(formatRef)
    );

    return current.length > 0
      ? new Date(current[0].date_reminder).toLocaleDateString('en-CA')
      : validation ? "" : (today >= dateFormated ? "À ENVOYER" : "");
  }, [reminders]);

  // ─── getRefFacture ────────────────────────────────────────────────────────
  const getRefFacture = useCallback((numclient) => {
    const regex = numclient.includes('RX') ? /C\d+/ : /R\d+/;
    return numclient.match(regex)[0];
  }, []);

  // ─── isReminderNeeded ─────────────────────────────────────────────────────
  const isReminderNeeded = useCallback((date, validation, numclient) => {
    if (validation === 1) return false;

    const today = new Date();
    const date1st = new Date(date);
    const date2nd = new Date(date);
    date1st.setDate(date1st.getDate() + 15);
    date2nd.setDate(date2nd.getDate() + 30);

    const hasFirst = reminders.some(r => r.numclient === numclient && r.first_reminder === 1);
    const hasSecond = reminders.some(r => r.numclient === numclient && r.first_reminder === 0);

    return (today >= date1st && !hasFirst) || (today >= date2nd && !hasSecond);
  }, [reminders]);

  // ─── loadData ─────────────────────────────────────────────────────────────
  // Accepte des overrides optionnels pour éviter de lire un state périmé (ex: après resetData)
  const loadData = useCallback((overrides = {}) => {
    dispatch(changePdfFileName(""));

    const loadId = Symbol();
    currentLoadDataId.current = loadId;

    const sd = overrides.startDate !== undefined ? overrides.startDate : startDate;
    const ed = overrides.endDate !== undefined ? overrides.endDate : endDate;
    const filters = overrides.filtersStatut !== undefined ? overrides.filtersStatut : filtersStatut;

    try {
      let data = [...factures]
        .sort((a, b) => (a.nfacture < b.nfacture ? -1 : 1))
        .map(item => ({
          nfacture: item.nfacture,
          ndossier: item.ndossier,
          // ✅ FIX : JSON.parse uniquement si item.dossier est encore une string
          dossier: safeParseJson(item.dossier),
          numclient: item.numclient,
          month_facture: {
            month: handleChangeDateToMonth(item.date_ech),
            ref: getCodeFacture(item.numclient),
          },
          date_ech: formatDate(item.date_ech),
          first_reminder: handleGetDateReminder(item, getRefFacture(item.numclient), 1),
          second_reminder: handleGetDateReminder(item, getRefFacture(item.numclient), 0),
          payment_date: formatDate(item.payment_date),
          validation: item.validation,
          contrat_valide: item.contrat_valide,
          amount_ht: item.total_ht,
          amount_tva: item.total_ht * (item.tva / 100),
          amount_ca: item.bank === 'ca'
            ? item.total_ht + item.total_ht * (item.tva / 100) : "",
          amount_bred: item.bank !== 'ca'
            ? item.total_ht + item.total_ht * (item.tva / 100) : "",
        }));

      // ─── Filtres statut ───────────────────────────────────────────────────
      const anyFilter = Object.values(filters).some(Boolean);
      if (anyFilter) {
        data = data.filter(item => {
          if (filters.paye && item.validation === 1) return true;
          if (filters.nonPaye && (item.validation === 0 || item.validation === null)) return true;
          if (filters.toSend && isReminderNeeded(item.date_ech, item.validation, item.numclient)) return true;
          if (filters.firstReminder && item.first_reminder !== "" && item.first_reminder !== "À ENVOYER") return true;
          if (filters.secondReminder && item.second_reminder !== "" && item.second_reminder !== "À ENVOYER") return true;
          return false;
        });
      }

      // ─── Filtre dates ─────────────────────────────────────────────────────
      if (sd !== "" && ed !== "") {
        const start = new Date(sd);
        const end = new Date(ed);
        data = data.filter(item => {
          const d = new Date(item.date_ech);
          return d >= start && d <= end;
        });
      }

      const newTotal = getTotalAmount(data);
      const { bredAmount, caAmount } = getTotalAmountByBank(data);

      if (isMounted.current && loadId === currentLoadDataId.current) {
        setFacturesData(data);
        setTotalAmount(newTotal);
        setTotalBredAmount(bredAmount);
        setTotalCaAmount(caAmount);
        setFile(null);
      }
    } catch (error) {
      console.error("loadData error:", error);
    }
  }, [
    dispatch, factures, startDate, endDate, filtersStatut,
    formatDate, handleGetDateReminder, getRefFacture,
    isReminderNeeded, getTotalAmount, getTotalAmountByBank,
  ]);

  // ─── componentDidMount ────────────────────────────────────────────────────
  useEffect(() => {
    isMounted.current = true;
    dispatch(changePdfFileName(""));

    const init = async () => {
      await dispatch(fetchAllReminder());
      if (factures.length === 0) dispatch(fetchAllFac());
      else loadData();
    };
    init();

    return () => { isMounted.current = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── componentDidUpdate (factures / reminders) ────────────────────────────
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    loadData();
  }, [factures, reminders]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers événements ──────────────────────────────────────────────────
  const handleDateChange = useCallback((event) => {
    const { name, value } = event.target;
    if (name === 'startDate') setStartDate(value);
    else if (name === 'endDate') setEndDate(value);
  }, []);

  const handleStatutChange = useCallback((event) => {
    const { name, checked } = event.target;
    setFiltersStatut(prev => ({ ...prev, [name]: checked }));
  }, []);

  // resetData passe les valeurs réinitialisées en overrides pour éviter le state périmé
  const resetData = useCallback(() => {
    const defaultFilters = {
      firstReminder: false, secondReminder: false,
      toSend: false, paye: false, nonPaye: false,
    };
    setStartDate('');
    setEndDate('');
    setFiltersStatut(defaultFilters);
    loadData({ startDate: '', endDate: '', filtersStatut: defaultFilters });
  }, [loadData]);

  // ─── handleGenClick ───────────────────────────────────────────────────────
  const handleGenClick = useCallback(async () => {
    setFile(null);
    setDisableButton(true);

    const requestOptions = {
      method: "POST",
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        data: facturesData,
        total_amount: totalAmount,
        today: new Date().toLocaleDateString('fr-FR').replace(/\//g, '-'),
        total_ttc_bred: totalBredAmount,
        total_ttc_ca: totalCaAmount,
      }),
    };

    const blob = await genSyntheseTablePDF(requestOptions);
    if (blob) {
      setFile(blob);
      setDisableButton(false);
      setOpenFile(true);
    }
  }, [facturesData, totalAmount, totalBredAmount, totalCaAmount]);

  // ─── handleShowPdf ────────────────────────────────────────────────────────
  const handleShowPdf = useCallback(async () => {
    setFile(null);
    setDisableButton(true);
    const response = await getSyntheseTablePDF('pdf');

    if (response.blob.size > 100) {
      setFile(response.blob);
      setOpenFile(true);
      dispatch(changePdfFileName(response.fileName));
    }
    setDisableButton(false);
  }, [dispatch]);

  // ─── handleGetPdfFile ─────────────────────────────────────────────────────
  const handleGetPdfFile = useCallback(async () => {
    setFile(null);
    const response = await getSyntheseTablePDF('pdf');

    if (response.blob.size > 100) {
      setFile(response.blob);
      dispatch(changePdfFileName(response.fileName));
    }
  }, [dispatch]);

  // ─── handleGetWordFile ────────────────────────────────────────────────────
  const handleGetWordFile = useCallback(async () => {
    setFile(null);
    const response = await getSyntheseTablePDF('word');
    const wordType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    if (response.blob.type === wordType) {
      setFile(response.blob);
      dispatch(changePdfFileName(response.fileName));
    }
  }, [dispatch]);

  // ─── closeDoc ─────────────────────────────────────────────────────────────
  const closeDoc = useCallback(() => {
    setOpenFile(false);
    setFile(null);
  }, []);

  // ─── handleChangeTitleName ────────────────────────────────────────────────
  const handleChangeTitleName = useCallback(() => {
    dispatch(changeTitleName("Tableau Synthèse de Facturation"));
  }, [dispatch]);

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div className="facture-global">
      <Header />
      <DocMenu
        file={file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
      />
      <h1 className="title">Tableau Synthèse de Facturation</h1>
      <ListTable
        facturesData={facturesData}
        totalAmount={totalAmount}
        totalBredAmount={totalBredAmount}
        totalCaAmount={totalCaAmount}
        disableButton={disableButton}
        startDate={startDate}
        endDate={endDate}
        filtersStatut={filtersStatut}
        resetData={resetData}
        loadData={loadData}
        handleDateChange={handleDateChange}
        handleStatutChange={handleStatutChange}
        handleGenClick={handleGenClick}
        handleShowPdf={handleShowPdf}
      />
      <GenPdf
        file={file}
        isOpen={openFile}
        closeDoc={closeDoc}
        scale={file !== null ? 1 : 0.5}
      />
    </div>
  );
};

export default Global;